import { _notNil } from '@/littledash.ts';
import { type ConsumptionType } from '@/model/Consumption.model.ts';
import { type WorkflowIntakeSetup } from '@/components/Workflow/Show/Workflow.model.ts';
import { type ISODateTime } from '@/model/Common.model';

export const DEFAULT_INTAKE_ORDER: Array<ConsumptionType> = ['fluid', 'food'];

export const DEFAULT_WORKFLOW_INTAKE: WorkflowIntakeSetup = {
  foodEnabled: false,
  fluidEnabled: false,
  timeEnabled: false,
  order: DEFAULT_INTAKE_ORDER,
};

export const isAnyIntakeEnabled = (intake: WorkflowIntakeSetup): boolean => intake.foodEnabled || intake.fluidEnabled;

export const ANIMAL_FOOD_INTAKE = {
  id: 'animal_food_intake',
  name: 'Animal Food Intake',
  unit: 'g',
  formula: 'animal_food_intake',
  measurements: [
    {
      id: 'animal_food_intake',
      name: 'Animal Food Intake',
      unit: 'g',
      formula: 'value',
    },
  ],
};

export const ANIMAL_FLUID_INTAKE = {
  id: 'animal_fluid_intake',
  name: 'Animal Fluid Intake',
  unit: 'g',
  formula: 'animal_fluid_intake',
  measurements: [
    {
      id: 'animal_fluid_intake',
      name: 'Animal Fluid Intake',
      unit: 'g',
      formula: 'value',
    },
  ],
};

export const consumptionMetrics = [ANIMAL_FOOD_INTAKE, ANIMAL_FLUID_INTAKE];

type IntakeRequestFields = {
  consumption_out?: number;
  consumption_in?: number;
  spillage?: number;
  comment?: string;
  type: ConsumptionType;
  recorded_at?: string | null;
  errorMessage?: string;
  reason_for_change?: string;
};

export const parseFormFields = (
  { fields }: { fields: Record<string, number | string | null> },
  inputInDisabled: boolean,
  inputOutDisabled: boolean,
  lastIn: number | null,
  type: ConsumptionType,
  measured_at: ISODateTime | null,
  reason_for_change: string | null
) => {
  const variables: IntakeRequestFields = {
    type: type,
    ...(measured_at ? { recorded_at: measured_at } : {}),
  };

  if (_notNil(fields.input_out) && Number(fields.input_out) >= 0 && !inputOutDisabled) {
    variables.consumption_out = Number(fields.input_out);
  }
  if (fields.spillage && !inputOutDisabled) {
    variables.spillage = Number(fields.spillage);
  }
  if (_notNil(fields.input_in) && Number(fields.input_in) > 0 && !inputInDisabled) {
    variables.consumption_in = Number(fields.input_in);
  }
  if (fields.comment) {
    variables.comment = String(fields.comment);
  }
  if (reason_for_change) {
    variables.reason_for_change = reason_for_change;
  }

  if (_notNil(lastIn)) {
    const remaining: number = Number(fields.input_out ?? 0) + Number(fields.spillage ?? 0);
    if (remaining > lastIn) {
      fields.errorMessage = `Remaining ${type} plus spillage cannot exceed last ${type} in recorded`;
      return fields;
    }
  }
  return variables;
};

export const consumptionMetricIds = new Set(consumptionMetrics.map((metric) => metric.id));

type IntakeLabelsType = {
  in: string;
  out: string;
  label: string;
  graphDisplay: string;
};

export const intakeLabels: Record<ConsumptionType, IntakeLabelsType> = {
  food: {
    in: 'Food In',
    out: 'Food Out',
    label: 'Food',
    graphDisplay: 'Cage Food Intake',
  },
  fluid: {
    in: 'Fluid In',
    out: 'Fluid Out',
    label: 'Fluid',
    graphDisplay: 'Cage Fluid Intake',
  },
} as const;
